/**
 * OCPP-J wire frame shapes (OCPP 1.6J — identical envelope in 2.0.1 / 2.1).
 *
 *   CALL        [2, messageId, action, payload]
 *   CALLRESULT  [3, messageId, payload]
 *   CALLERROR   [4, messageId, errorCode, errorDescription, errorDetails]
 *
 * Domain code never touches these directly — the adapter sits between domain
 * intents and these tuples.
 */

export const OCPP_MESSAGE_TYPE = {
  CALL: 2,
  CALLRESULT: 3,
  CALLERROR: 4,
} as const;
export type OcppMessageType = (typeof OCPP_MESSAGE_TYPE)[keyof typeof OCPP_MESSAGE_TYPE];

export type OcppCallFrame = readonly [
  typeof OCPP_MESSAGE_TYPE.CALL,
  string,
  string,
  Record<string, unknown>,
];

export type OcppCallResultFrame = readonly [
  typeof OCPP_MESSAGE_TYPE.CALLRESULT,
  string,
  Record<string, unknown>,
];

export type OcppCallErrorFrame = readonly [
  typeof OCPP_MESSAGE_TYPE.CALLERROR,
  string,
  string,
  string,
  Record<string, unknown>,
];

export type OcppFrame = OcppCallFrame | OcppCallResultFrame | OcppCallErrorFrame;

/**
 * Logical outbound CALL: the adapter builds the frame, hands it to the
 * transport, and awaits a CALLRESULT for the same messageId.
 */
export interface OcppCall<Req = Record<string, unknown>, Res = Record<string, unknown>> {
  readonly messageId: string;
  readonly action: string;
  readonly request: Req;
  /**
   * Resolves with the parsed response payload once CALLRESULT arrives;
   * rejects with OcppCallError on CALLERROR or OcppTimeoutError on timeout.
   */
  readonly response: Promise<Res>;
}

/** OCPP-defined error codes for CALLERROR frames. */
export const OCPP_ERROR_CODES = [
  'NotImplemented',
  'NotSupported',
  'InternalError',
  'ProtocolError',
  'SecurityError',
  'FormationViolation',
  'PropertyConstraintViolation',
  'OccurrenceConstraintViolation',
  'TypeConstraintViolation',
  'GenericError',
] as const;
export type OcppErrorCode = (typeof OCPP_ERROR_CODES)[number];
